// Location-Safe Lint
//
// Client-side heuristic checks that scan post fields for phrases that might
// accidentally reveal a hunting location (addresses, GPS coordinates, parcel
// numbers, road names, map links, etc). This is a WARNING system, never a
// guarantee — the owner can acknowledge and publish anyway.

const STREET_TYPES =
	'(road|rd|street|st|lane|ln|highway|hwy|route|rte|trail|drive|dr|avenue|ave|boulevard|blvd|court|ct|way|path|circle|cir|terrace|ter|place|pl|parkway|pkwy|loop|run|hollow|ridge|creek)';

const PATTERNS = [
	{
		id: 'full_address',
		label: 'Full street address',
		reason: 'Looks like a full street address with a number.',
		regex: new RegExp(`\\b\\d{1,5}\\s+[A-Za-z][A-Za-z]+(?:\\s+[A-Za-z][A-Za-z]+){0,2}\\s+${STREET_TYPES}\\b`, 'gi'),
	},
	{
		id: 'gps_decimal',
		label: 'GPS coordinates',
		reason: 'Looks like decimal latitude / longitude.',
		regex: /(-?\d{1,3}\.\d{3,},\s*-?\d{1,3}\.\d{3,})/g,
	},
	{
		id: 'gps_dms',
		label: 'GPS coordinates (DMS)',
		reason: 'Looks like degrees\u2013minutes\u2013seconds coordinates.',
		regex: /(\d{1,3}[\u00b0]\s?\d{1,2}['\u2032]\s?\d{1,2}(?:\.\d+)?["\u2033]?\s?[NnSs][,\s]+\d{1,3}[\u00b0]\s?\d{1,2}['\u2032]\s?\d{1,2}(?:\.\d+)?["\u2033]?\s?[EeWw])/g,
	},
	{
		id: 'plus_code',
		label: 'Plus Code',
		reason: 'Looks like a Google Plus Code (open location code).',
		regex: /\b[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3}\b/g,
	},
	{
		id: 'parcel_number',
		label: 'Parcel or tax ID number',
		reason: 'Looks like a parcel ID, tax ID, or property number.',
		regex: /\b(?:parcel|tax\s*id|apn|pin|property\s*id)\s*#?\s*[:\-]?\s*[A-Z0-9\-]{4,}\b/gi,
	},
	{
		id: 'township_range',
		label: 'Township / Range / Section',
		reason: 'PLSS township / range / section descriptor.',
		regex: /\bT(?:ownship)?\s*\d{1,3}[NSns]?\s*[,\-]?\s*R(?:ange)?\s*\d{1,3}[EWew]?\b/gi,
	},
	{
		id: 'section_number',
		label: 'Section number (PLSS)',
		reason: 'Looks like a PLSS section number.',
		regex: /\bSection\s+(?:[1-9]|[1-2]\d|3[0-6])\b/gi,
	},
	{
		id: 'map_link',
		label: 'Map-sharing link',
		reason: 'Public map link may reveal a location.',
		regex: /\bhttps?:\/\/(?:www\.)?(?:google\.[^\s/]+\/maps|maps\.google\.[^\s/]+|goo\.gl\/maps|maps\.app\.goo\.gl|onxmaps\.com|huntstand\.com|basemap\.com|gaiagps\.com|caltopo\.com|openstreetmap\.org|apple\.com\/maps)\S*/gi,
	},
	{
		id: 'trailhead',
		label: 'Named trailhead',
		reason: 'Named trailhead can identify an exact access point.',
		regex: /\b(?:[A-Z][a-zA-Z]+\s+){1,3}Trailhead\b/g,
	},
	{
		id: 'wildlife_unit',
		label: 'Named wildlife management area or compartment',
		reason: 'Named public-land unit / compartment can identify a location.',
		regex: /\b(?:[A-Z][a-zA-Z]+\s+){1,3}(?:WMA|Wildlife\s+Management\s+Area|State\s+Forest|Compartment|National\s+Forest\s+Unit)\b/g,
	},
	{
		id: 'parking',
		label: 'Named parking lot',
		reason: 'Named parking area or lot can identify an exact access point.',
		regex: /\b(?:parking\s+(?:lot|area)\s+(?:at|by|near)\s+[A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?|(?:[A-Z][a-zA-Z]+\s+){1,3}(?:Parking\s+Lot|Parking\s+Area))\b/g,
	},
];

const SAFE_PHRASES = /^(public road|back trail|county land|the trail|the road|state land|public land)$/i;

export function scanText(text, source) {
	if (!text || typeof text !== 'string') return [];
	const issues = [];
	for (const p of PATTERNS) {
		const re = new RegExp(p.regex.source, p.regex.flags);
		let m;
		while ((m = re.exec(text)) !== null) {
			const match = m[0];
			if (SAFE_PHRASES.test(match.trim())) continue;
			issues.push({
				id: `${p.id}-${source}-${m.index}`,
				source,
				label: p.label,
				reason: p.reason,
				match,
				index: m.index,
			});
			if (!re.global) break;
		}
	}
	return issues;
}

/** Scan a whole post object. `fields` is an array of { key, label, value } */
export function scanPost(fields) {
	const all = [];
	for (const f of fields) {
		if (!f || !f.value) continue;
		const arr = scanText(String(f.value), f.label);
		all.push(...arr);
	}
	return all;
}

/** Visible reminder about photo metadata that isn't EXIF */
export const PHOTO_METADATA_NOTICE =
	'Even with EXIF removed, visible signs, license plates, tags, mailboxes, maps, and landmarks inside a photo can reveal a location. Please review your images.';
